import Dropdown from "components/forms/inputs/dropdown";
import TextInput from "components/forms/inputs/text";
import { useLocale } from "stores/hooks/locale";
import { parse } from "lib/locale";
import { ListEntityField } from "models/list-entity-field";
import { Fragment, FunctionComponent } from "react";
import Grid from "./grid";
import Row from "./row";
import Col from "./col";
import Checkbox from "components/forms/inputs/checkbox";
import { useIsAdmin } from "stores/hooks/auth";
import Datepicker from "components/forms/inputs/datepicker";

interface FieldGridProps {
  listEntityFields: ListEntityField[];
}

const FieldGrid: FunctionComponent<FieldGridProps> = ({ listEntityFields }) => {
  const isAdmin = useIsAdmin();
  const locale = useLocale();

  const fieldRows: ListEntityField[][] = [];
  let row: ListEntityField[] = [];
  listEntityFields.forEach((field, index) => {
    // if the row has space, add field
    if (row.length <= 2) {
      row.push(field);
    }

    // if the row is full, add to collection (and reset row)
    if (row.length === 2 || index === listEntityFields.length - 1) {
      fieldRows.push(row);
      row = [];
    }
  });

  const buildField = (field: ListEntityField) => {
    const {
      uuid,
      name,
      datatype,
      options = [],
      required,
      admin_only: admin,
    } = field;
    const label = parse(name, locale);
    switch (datatype) {
      case "text":
      case "email":
      case "ssn":
      case "phone":
        return (
          <TextInput
            name={uuid}
            label={label}
            disabled={admin && !isAdmin}
            required={required}
            uppercase={datatype === "ssn"}
          />
        );
      case "dropdown":
        return (
          <Dropdown
            name={uuid}
            label={label}
            disabled={admin && !isAdmin}
            options={(options || []).map((o) => {
              return { value: o.value, text: parse(o.text, locale) };
            })}
            required={required}
          />
        );
      case "check":
        return (
          <Checkbox
            string
            name={uuid}
            label={label}
            required={required}
            disabled={admin && !isAdmin}
          />
        );
      case "date":
      case "datetime":
        return (
          <Datepicker
            time={datatype === "datetime"}
            name={uuid}
            label={label}
            required={required}
            disabled={admin && !isAdmin}
          />
        );
    }
    return <div>not supported yet</div>;
  };

  // check row lengths
  const gridRows: JSX.Element[][] = [];
  fieldRows.forEach((row) => {
    const [field1, field2] = row;

    // special case, the row only has 1 item
    if (field2 === undefined) {
      const row = [buildField(field1)];
      if (field1.large === false) {
        row.push(<Fragment />);
      }

      return gridRows.push(row);
    }

    // if both are small
    if (!field1.large && !field2.large) {
      return gridRows.push([buildField(field1), buildField(field2)]);
      // either is large or both are large ==> split row
    } else {
      const row1 = [buildField(field1)];
      if (field1.large === false) {
        row1.push(<Fragment />);
      }
      const row2 = [buildField(field2)];
      if (field2.large === false) {
        row2.push(<Fragment />);
      }
      return gridRows.push(row1, row2);
    }
  });

  return (
    <Grid>
      {gridRows.map((gridRow, rowIndex) => {
        return (
          <Fragment key={rowIndex}>
            <Row>
              {gridRow.map((field, columnIndex) => {
                return <Col key={columnIndex}>{field}</Col>;
              })}
            </Row>
          </Fragment>
        );
      })}
    </Grid>
  );
};

export default FieldGrid;
